// Settings > Onboarding > Privacy & consent: what the site tag (gt.js) and
// the embeds do about the shopper's cookie consent (API lib/tagConsent; plan
// gtjs-consent-aware). gt.js reads the store's consent tooling itself; this
// sets the fallback for when none answers, and shows the one-line hook for
// a custom banner.
import React, { useEffect, useState } from 'react'
import { MenuItem, TextField } from '@mui/material'
import toast from 'react-hot-toast'
import axiosInstant from '../../../../api'
import { getBrand } from '../../../../api/endpoints'
import { Snippet } from '../GoogleTag'
import { useUser } from '../../../../hooks/useUser'
import { isInternal } from '../../../../utils'

export type TagConsentMode = 'regional' | 'always' | 'never'

// The tag never shows a banner or asks anything: it reads the choice the
// store's own cookie banner recorded. This is what it does when it can't
// find one (no banner, or the shopper hasn't answered).
const MODES: { value: TagConsentMode; label: string; help: string }[] = [
  {
    value: 'regional',
    label: 'Track anonymously in the EU, UK, Switzerland and Quebec (recommended)',
    help:
      'Where privacy law requires permission first, shoppers are counted anonymously until your cookie banner records a yes. Everywhere else they are tracked normally, unless they opt out (for example with Global Privacy Control).',
  },
  {
    value: 'always',
    label: 'Track anonymously everywhere',
    help: 'Every shopper is counted anonymously until your cookie banner records a yes.',
  },
  {
    value: 'never',
    label: 'Track normally everywhere',
    help:
      "Shoppers are tracked normally unless your cookie banner or their browser records a no. You're responsible for making sure this is allowed where your shoppers are.",
  },
]

// Works before and after the tag loads: once loaded, tdcGTag.consent is a
// function (consent.js); before, the value is left for it to read.
const consentCall = (v: 'granted' | 'denied') =>
  `window.tdcGTag = window.tdcGTag || {}; typeof window.tdcGTag.consent === 'function' ? window.tdcGTag.consent('${v}') : (window.tdcGTag.consent = '${v}');`
const GRANT_SNIPPET = consentCall('granted')
const DENY_SNIPPET = consentCall('denied')
const PRELOAD_SNIPPET = "<script>window.tdcGTag = Object.assign(window.tdcGTag || {}, { consent: 'pending' });</script>"

export default function ConsentSettings({ brandId, mode, onSaved }: { brandId: number; mode: TagConsentMode; onSaved: () => void }) {
  const { role } = useUser()
  // A brand-wide privacy decision: brand admins and staff (the API agrees).
  const canEdit = isInternal(String(role)) || role === 'BRAND_ADMIN'
  const [value, setValue] = useState<TagConsentMode>(mode)
  const [saving, setSaving] = useState(false)
  useEffect(() => setValue(mode), [mode])

  const save = async (next: TagConsentMode) => {
    const prev = value
    setValue(next)
    setSaving(true)
    try {
      await axiosInstant.patch(getBrand(brandId), { tag_consent_mode: next })
      toast.success('Consent setting saved.')
      onSaved()
    } catch {
      setValue(prev)
      toast.error("Couldn't save the consent setting.")
    } finally {
      setSaving(false)
    }
  }

  const current = MODES.find((m) => m.value === value) || MODES[0]

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-text-2 max-w-[720px]">
        The site tag doesn&rsquo;t show a cookie banner or ask shoppers anything. It reads the choice your own cookie
        banner records. If a shopper says no, the tag still counts video plays and watch time anonymously, but keeps
        nothing that identifies them and doesn&rsquo;t link their visit to a later purchase.
      </p>
      <TextField
        select
        size="small"
        label="If your cookie banner hasn't recorded a choice"
        value={value}
        onChange={(e) => save(e.target.value as TagConsentMode)}
        disabled={saving || !canEdit}
        helperText={canEdit ? current.help : `${current.help} Only a brand admin can change this.`}
        sx={{ maxWidth: 520 }}
      >
        {MODES.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            {m.label}
          </MenuItem>
        ))}
      </TextField>
      <div className="text-[13px] text-text-2 max-w-[720px] flex flex-col gap-1">
        <strong className="text-text-1">Cookie banners the tag reads on its own</strong>
        <span>
          Shopify&rsquo;s cookie banner, Google Consent Mode, IAB TCF and GPP banners (OneTrust, Cookiebot, Didomi,
          Usercentrics and others), and the browser&rsquo;s Global Privacy Control. In OneTrust, Cookiebot and similar
          tools, add The Desire Company under your <strong>Analytics / Statistics</strong> category.
        </span>
      </div>
      <details className="text-[13px] text-text-2 max-w-[720px]">
        <summary className="cursor-pointer font-semibold text-text-1">Using a custom cookie banner?</summary>
        <div className="mt-2 flex flex-col gap-1">
          <span>Call one of these when the shopper makes their choice:</span>
          <Snippet label="On accept" code={GRANT_SNIPPET} />
          <Snippet label="On decline" code={DENY_SNIPPET} />
          <span>
            To have the tag wait for the shopper&rsquo;s answer before sending anything, put this before the site tag:
          </span>
          <Snippet label="Before the site tag" code={PRELOAD_SNIPPET} />
        </div>
      </details>
    </div>
  )
}
